<template>
  <div v-if="carId && (loading || car)" class="roof-rack-block">
    <div v-if="loading" class="preselect__spinner-container">
      <span class="preselect__spinner" aria-hidden="true" />
    </div>
    <template v-else-if="car">
      <FlashMessageItem :id="0" level="warning" :message="explanationMessage" :timeout="0" />
      <div class="roof-rack__container">
        <div class="roof-rack__group-list">
          <div v-for="group in productVisibleGroups" :key="group" class="linked_products">
            <h3 class="heading--h3">
              <span>{{ visibleGroups.indexOf(group) + 1 }}. </span>
              <span>{{ t(`linked.selection_${group}`) }}</span>
              <span v-if="groups[group]?.optional">
                &nbsp;({{ tCategory('selection.optional') }})
              </span>
            </h3>
            <div class="roof-rack__product-container">
              <div
                v-for="link in sortedProducts(group)"
                :key="`${group}-${link.product.entity_id}`"
                class="roof-rack__product"
              >
                <template v-if="shouldShowProduct(link, group)">
                  <button
                    type="button"
                    class="roof-rack__product-select"
                    @click.prevent="selectProduct(group, link)"
                  >
                    <div
                      class="thumbnail__container"
                      :class="{
                        'roof-rack__product--selected': selectedProducts[group] === link.product,
                      }"
                    >
                      <div class="thumbnail__image-container thumbnail__image-container--centered">
                        <img
                          class="thumbnail__image thumbnail__image--centered"
                          :src="link.product.image_xs"
                          :alt="link.product.name"
                          :title="link.product.name"
                        />
                        <div
                          v-if="link.product.pre_orderable || link.product.aantal_stuks"
                          class="thumbnail__badge badge__container badge__container--primary"
                        >
                          <span v-if="link.product.aantal_stuks" class="thumbnail__badge-count">
                            {{ link.product.aantal_stuks }}
                          </span>
                          <span class="thumbnail__badge-label">
                            <template v-if="link.product.pre_orderable">
                              {{ tProduct('product.pre_orderable') }}
                            </template>
                            <template v-else-if="link.product.aantal_stuks">
                              {{ tProduct('product.aantal_stuks') }}
                            </template>
                          </span>
                        </div>
                      </div>
                      <span class="thumbnail__title">{{ link.product.name }}</span>
                      <template v-if="!link.product.salable">
                        <span class="thumbnail__special-price">
                          {{ tProduct('product.not_salable') }}
                        </span>
                      </template>
                      <template
                        v-else-if="
                          link.product.show_special_price &&
                          link.product.special_price &&
                          link.product.special_price < link.product.price
                        "
                      >
                        <span class="thumbnail__price thumbnail__price--line-through">
                          {{ formatPrice(link.product.price) }}
                        </span>
                        <span class="thumbnail__special-price">
                          {{ formatPrice(link.product.special_price) }}
                        </span>
                      </template>
                      <template v-else>
                        <span class="thumbnail__price">
                          {{ formatPrice(link.product.special_price || link.product.price) }}
                        </span>
                      </template>
                    </div>
                  </button>
                  <a :href="link.product.uri" class="anchor roof-rack__product-link">
                    {{ t('general.more_info') }}
                  </a>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="roof-rack__selection">
          <h3>{{ tCategory('selection.your_selection') }}</h3>
          <div
            v-for="group in cartVisibleGroups"
            :key="`summary-${group}`"
            class="roof-rack__summary-item"
          >
            <template v-if="selectedProducts[group]">
              <img
                :src="selectedProducts[group]!.image_xs"
                class="roof-rack__summary-image"
                :alt="selectedProducts[group]!.name"
              />
              <a
                class="roof-rack__summary-text"
                :href="selectedProducts[group]!.uri || selectedProducts[group]!.url"
              >
                <span class="roof-rack__summary-name">{{ selectedProducts[group]!.name }}</span>
                <span class="roof-rack__summary-delivery">
                  {{ selectedProducts[group]!.delivery_time }}
                </span>
                <span class="roof-rack__summary-price">
                  {{
                    formatPrice(
                      selectedProducts[group]!.special_price || selectedProducts[group]!.price
                    )
                  }}
                </span>
              </a>
            </template>
            <template v-else>
              <span class="roof-rack__summary-number">
                {{ visibleGroups.indexOf(group) + 1 }}
              </span>
              <div class="roof-rack__summary-text">
                <span>{{ tCategory(`selection.no_${group}`) }}</span>
                <template v-if="groups[group]?.optional">
                  &nbsp;({{ tCategory('selection.optional') }})
                </template>
              </div>
            </template>
          </div>
          <div class="roof-rack__to-cart">
            <div class="heading-h2 roof-rack__total-price">{{ formatPrice(totalPrice) }}</div>
            <button
              type="button"
              class="button button--secondary"
              :disabled="!canAddToCart || submitting"
              @click="addToCart"
            >
              <i class="icon icon--basket icon--showleft" />
              <span>{{ tOrder('cart.add_to_cart') }}</span>
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, onMounted, reactive, ref } from 'vue';

import FlashMessageItem from '@shop/components/ui/FlashMessageItem.vue';
import { useFlash } from '@shop/composables/useFlash';
import { generateUrl } from '@shop/utils/Routing';
import formatPrice from '@shop/utils/Price';
import { useTranslation } from '@shop/utils/Translation';

import type {
  ProductTypeTranslation,
  RoofRackCar,
  RoofRackGroupConfig,
  RoofRackGroupKey,
  RoofRackLink,
  RoofRackProduct,
} from './RoofRackBlock.types';

const { t } = useTranslation('roofrack_tool');
const { t: tCategory } = useTranslation('category');
const { t: tOrder } = useTranslation('order');
const { t: tProduct } = useTranslation('product');
const flash = useFlash();

const carId = ref<number | null>(null);
const car = ref<RoofRackCar | null>(null);
const loading = ref(false);
const submitting = ref(false);

const selectedBrand = ref<string | null>(null);
const selectedGroup = ref<RoofRackGroupKey | null>(null);
const selectedBar = ref(false);
const selectedSerie = ref<string | null>(null);
const selectedProducts = reactive<Partial<Record<RoofRackGroupKey, RoofRackProduct>>>({});
const productTypeTranslations = reactive<Record<string, ProductTypeTranslation[]>>({});

const groups: Record<RoofRackGroupKey, RoofRackGroupConfig> = {
  roofrack: {
    attributeCodes: [
      'geintegreerde_voetenset_dd',
      't_tracks_dd',
      'aerodynamisch_dd',
      'uitschuifbaar_dd',
      'ligging_dak_dd',
      'maximaal_laadvermogen_kg',
    ],
  },
  stang: {
    attributeCodes: ['short_description'],
  },
  foot: {
    attributeCodes: ['short_description'],
  },
  kit: {
    attributeCodes: ['short_description'],
  },
  sra: {
    attributeCodes: ['short_description'],
  },
  roofbox: {
    optional: true,
  },
};

const explanationMessage = computed(() =>
  t.value('general.explanation', {
    carName: `${car.value?.brand ?? ''} ${car.value?.model ?? ''}`.trim(),
  })
);

const getLinkedAttribute = (group: RoofRackGroupKey): keyof RoofRackCar => {
  return `linked_${group}s` as keyof RoofRackCar;
};

const linksForGroup = (group: RoofRackGroupKey): RoofRackLink[] => {
  if (!car.value) {
    return [];
  }
  const raw = car.value[getLinkedAttribute(group)];
  if (!raw) {
    return [];
  }
  return Array.isArray(raw) ? raw : Object.values(raw);
};

const shouldShowProduct = (link: RoofRackLink, group: RoofRackGroupKey): boolean => {
  if (!selectedBrand.value) {
    return true;
  }
  if (group === selectedGroup.value) {
    return true;
  }
  return selectedBrand.value === link.product.manufacturer;
};

const checkProductType = (productType: string | undefined, type: string): boolean => {
  if (!productType) {
    return false;
  }
  const translations = productTypeTranslations[type] ?? [];
  return translations.some(item => item.value === productType);
};

const visibleGroups = computed((): RoofRackGroupKey[] => {
  if (!car.value) {
    return [];
  }

  const result: RoofRackGroupKey[] = [];
  (Object.keys(groups) as RoofRackGroupKey[]).forEach(key => {
    if (selectedBar.value && key === 'stang') {
      result.push(key);
      return;
    }

    if (key === 'kit') {
      const hasVisibleKit = linksForGroup(key).some(link => shouldShowProduct(link, key));
      if (hasVisibleKit) {
        result.push(key);
      }
      return;
    }

    const integratedFeet =
      (selectedProducts.roofrack || ({} as RoofRackProduct)).geintegreerde_voetenset_dd === 'Ja';
    if (key !== 'foot' || !integratedFeet) {
      if (linksForGroup(key).length > 0) {
        result.push(key);
      }
    }
  });

  return result;
});

const productVisibleGroups = computed(() => visibleGroups.value.filter(group => group !== 'stang'));

const cartVisibleGroups = computed(() => visibleGroups.value);

const sortedProducts = (key: RoofRackGroupKey): RoofRackLink[] => {
  const links = linksForGroup(key);
  const positionCounts: Record<number, number> = {};

  links.forEach(link => {
    const position = link.position ?? 0;
    positionCounts[position] = (positionCounts[position] || 0) + 1;
  });

  return links
    .map(link => {
      let order = link.position ?? 0;
      const score = link.product.score || 0;
      const salable = link.product.salable || false;
      const count = positionCounts[link.position ?? 0] || 1;

      if (!salable) {
        order += 99999;
      } else if (count > 1) {
        order -= score / 1000;
      }

      return { ...link, order };
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const clearSelectionStateIfEmpty = (): void => {
  if (Object.keys(selectedProducts).length === 0) {
    selectedBrand.value = null;
    selectedGroup.value = null;
    selectedSerie.value = null;
    selectedBar.value = false;
  }
};

const removeSelected = (group: RoofRackGroupKey): void => {
  delete selectedProducts[group];
  clearSelectionStateIfEmpty();
};

const autoSelectGroup = (key: RoofRackGroupKey): void => {
  if (selectedProducts[key] || !selectedSerie.value || !car.value) {
    return;
  }

  const sameSerieLinks = linksForGroup(key).filter(
    link =>
      selectedBrand.value === link.product.manufacturer &&
      selectedSerie.value === link.product.serie
  );

  if (sameSerieLinks.length === 1) {
    selectProduct(key, sameSerieLinks[0]);
  }
};

const selectProduct = (group: RoofRackGroupKey, link: RoofRackLink): void => {
  if (groups[group]?.optional === true) {
    if (selectedProducts[group] === link.product) {
      removeSelected(group);
      return;
    }
  }

  if (Object.keys(selectedProducts).length > 0 && (group === 'stang' || group === 'roofrack')) {
    if (selectedProducts[group] === link.product) {
      removeSelected(group);
      return;
    }

    const current = selectedProducts[group];
    if (selectedProducts.stang && checkProductType(current?.product_type, 'Stang')) {
      delete selectedProducts.stang;
      selectedBar.value = false;
    }
  }

  if (!link.product.salable) {
    return;
  }

  if (checkProductType(link.product.product_type, 'Stang') && link.product_link) {
    const linkedGroup = link.product.product_type!.toLowerCase() as RoofRackGroupKey;
    selectedProducts[linkedGroup] = link.product_link;
    selectedBar.value = true;
  }

  selectedProducts[group] = link.product;

  if (!selectedBrand.value) {
    selectedGroup.value = group;
  }
  selectedBrand.value = link.product.manufacturer ?? null;
  selectedSerie.value = link.product.serie ?? null;

  (Object.keys(selectedProducts) as RoofRackGroupKey[]).forEach(selectedKey => {
    const selected = selectedProducts[selectedKey];
    if (selected && selectedBrand.value !== selected.manufacturer) {
      delete selectedProducts[selectedKey];
    }
  });

  if (group === 'stang' || group === 'roofrack') {
    autoSelectGroup('foot');
  }
};

const totalPrice = computed(() => {
  let price = 0;
  visibleGroups.value.forEach(key => {
    const product = selectedProducts[key];
    if (!product) {
      return;
    }
    price += product.special_price || product.price || 0;
  });
  return price;
});

const canAddToCart = computed(() => {
  let canAdd = true;
  visibleGroups.value.forEach(key => {
    if (groups[key]?.optional === true) {
      return;
    }
    if (!selectedProducts[key]) {
      canAdd = false;
    }
  });
  return canAdd && totalPrice.value > 0;
});

const loadProductTypeTranslations = async (productType: string): Promise<void> => {
  try {
    const response = await axios.get<ProductTypeTranslation[]>(
      generateUrl('category_tool_roofrack_product_type', { product_type: productType })
    );
    productTypeTranslations[productType] = response.data ?? [];
  } catch {
    productTypeTranslations[productType] = [];
  }
};

const addToCart = async (): Promise<void> => {
  const productsToSubmit: { product: number; qty: number }[] = [];

  Object.values(selectedProducts).forEach(product => {
    if (!product) {
      return;
    }
    const existing = productsToSubmit.find(row => row.product === product.entity_id);
    if (existing) {
      existing.qty += 1;
    } else {
      productsToSubmit.push({ product: product.entity_id, qty: 1 });
    }
  });

  submitting.value = true;
  try {
    await axios.post(generateUrl('add_product'), { products: productsToSubmit });
    window.location.href = generateUrl('checkout_cart');
  } catch (error) {
    await flash.showApiError(error);
  } finally {
    submitting.value = false;
  }
};

onMounted(() => {
  void loadProductTypeTranslations('Stang');

  const params = new URLSearchParams(window.location.search);
  const rawCarId = params.get('car_id');
  carId.value = rawCarId ? parseInt(rawCarId, 10) : null;

  if (!carId.value) {
    return;
  }

  loading.value = true;
  axios
    .get<RoofRackCar>(generateUrl('category_tool_car_detail', { car: carId.value }))
    .then(response => {
      car.value = response.data ?? null;
    })
    .catch(async error => {
      await flash.showApiError(error);
    })
    .finally(() => {
      loading.value = false;
    });
});
</script>
